import { useEffect, useMemo, useState } from 'react';
import {
  Link,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  AppKeys,
  AppStatuses,
  LocStatusTemplate,
  ParsedStatuses,
} from '../../utils/types';
import {
  APP_DISPLAY_NAMES,
  APP_HYPERLINKS,
  APP_KEYS,
  INIT_APP_STATUSES,
  INIT_STATUS_BY_LOCATION,
  LOCATION_DISPLAY_NAMES,
  LOCATION_KEYS,
  STATUS_ICONS,
} from '../../utils/consts';
import { DOMAIN } from '../../api/api-consts';

const StatusTable = ({
  healthData,
}: {
  healthData: ParsedStatuses | undefined;
}) => {
  const [appStatuses, setAppStatuses] = useState<AppStatuses>({
    ...INIT_APP_STATUSES,
  });

  useEffect(() => {
    const newAppStatuses: AppStatuses = { ...INIT_APP_STATUSES };
    if (healthData) {
      //  iterate over app keys array and only use ones that are defined in APP_KEYS
      APP_KEYS.forEach(appKey => {
        // generate "server" app key from domain
        const serverAppKey = appKey + `.brainos.${DOMAIN}`;

        const locations = healthData?.apps?.[serverAppKey]?.locations ?? null;

        if (!locations || Object.keys(locations).length < 1) {
          console.error('missing locations data');
          // show 'unknown' for all locations
          newAppStatuses[appKey] = { ...INIT_STATUS_BY_LOCATION };
          return;
        }

        const currentAppStatuses = { ...INIT_STATUS_BY_LOCATION };

        LOCATION_KEYS.forEach(locKey => {
          currentAppStatuses[locKey] = locations[locKey]?.status || 'unknown';
        });

        newAppStatuses[appKey] = currentAppStatuses;
      });
    }
    // set app statuses regardless of health data
    setAppStatuses(newAppStatuses);
  }, [healthData]);

  const renderHeader = () => {
    return (
      <TableRow data-testid="statusTable">
        <StyledHeaderTableCell>
          <StyledHeaderTypogaphy variant="body1">Product</StyledHeaderTypogaphy>
        </StyledHeaderTableCell>
        {LOCATION_KEYS.map(locKey => {
          return (
            <StyledHeaderTableCell key={locKey}>
              <StyledHeaderTypogaphy variant="body1">
                {LOCATION_DISPLAY_NAMES[locKey]}
              </StyledHeaderTypogaphy>
            </StyledHeaderTableCell>
          );
        })}
      </TableRow>
    );
  };

  const renderAppStatuses = useMemo(() => {
    const appStatusesArray = Object.entries(appStatuses) as [
      AppKeys,
      LocStatusTemplate,
    ][];

    // alphabetical order by app key
    appStatusesArray.sort(([appKeyA], [appKeyB]) => {
      return appKeyA.localeCompare(appKeyB);
    });

    return appStatusesArray.map(([appKey, appStatuses]) => {
      return (
        <TableRow key={appKey} data-testid={`${appKey}Row`}>
          <TableCell>
            <Link
              href={APP_HYPERLINKS[appKey]}
              underline="always"
              rel="noopener"
              target="_blank"
              variant="body1"
              color="text.primary"
            >
              {APP_DISPLAY_NAMES[appKey]}
            </Link>
          </TableCell>
          {LOCATION_KEYS.map(locKey => {
            return (
              <TableCell key={locKey} data-testid={`${locKey}`}>
                {STATUS_ICONS[appStatuses[locKey]]}
              </TableCell>
            );
          })}
        </TableRow>
      );
    });
  }, [appStatuses]);

  return (
    // Paper element controls table dimensions
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer>
        <Table
          stickyHeader
          aria-label="sticky table"
          // equal width columns
          style={{ tableLayout: 'fixed' }}
          sx={{ backgroundColor: '#424242' }}
        >
          <TableHead>{renderHeader()}</TableHead>
          <TableBody>{renderAppStatuses}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const StyledHeaderTableCell = styled(TableCell)(() => ({
  backgroundColor: '#424242',
}));

const StyledHeaderTypogaphy = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px !important',
  },
}));

export default StatusTable;
